import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Breakpoints } from '@/utils/Breakpoint';
import { FlashcardStack } from './FlashcardStack';
import { FlashcardAnswerOption } from '../../typologies/flashcard/useFlashcardAnswer';
import { FlashcardAnswerBtn } from './FlashcardAnswerBtn';
import { FrontFlashcard } from './FrontFlashcard';
import { BackFlashcard } from './BackFlashcard';
import { useFlashcardSwipe } from './useFlashcardSwipe';

type AnsweringFlashcardViewProps = {
  frontContent: string;
  backContent: string;
  showFrontOnBack: boolean;
  showBack: boolean;
  revealBack: () => void;
  selectedValues: string;
  handleChange: (value: string) => void;
  options: FlashcardAnswerOption[];
  disabled: boolean;
};

export function AnsweringFlashcardView({
  frontContent,
  backContent,
  showFrontOnBack,
  showBack,
  revealBack,
  selectedValues,
  handleChange,
  options,
  disabled,
}: AnsweringFlashcardViewProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'questionView.flashcard',
  });
  const isMobile = useMediaQuery({ maxWidth: Breakpoints.md });

  const swipe = useFlashcardSwipe({
    onSwipe: handleChange,
    enabled: showBack,
  });

  return (
    <div className="flex flex-col items-center justify-center gap-x-4 gap-y-6">
      <h3 className="text-sm font-semibold text-primary">
        {showBack ? t('back') : t('front')}
      </h3>

      <FlashcardStack
        front={<FrontFlashcard content={frontContent} />}
        back={
          <BackFlashcard
            frontContent={frontContent}
            backContent={backContent}
            showFrontOnBack={showFrontOnBack}
          />
        }
        showBack={showBack}
        onFlip={revealBack}
        flipPlaceholder={t('flipCard')}
        swipe={swipe}
        draggable={isMobile}
      />

      <ConditionalRenderer condition={!isMobile && showBack}>
        <FlashcardAnswerBtn
          value={selectedValues}
          onChange={swipe.triggerSwipe}
          options={options}
          disabled={disabled}
        />
      </ConditionalRenderer>
    </div>
  );
}
